import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfWeek, addDays, addWeeks, isSameDay } from "date-fns";
import { useAuth } from "hooks/useAuth";
import {
  fetchScheduleItems, fetchScheduleItemsWithChangeRequests,
  fetchPartners, fetchUsers, fetchNotes,
  runScheduler, runRecipientScheduler,
} from "api";
import { PageHeaderWithBodyAction, StatusBadge, Spinner } from "components/common";
import type { ScheduleItem, Note } from "types";
import { SchedulerToolsMenu } from "./SchedulerToolsMenu";
import { FilterDropdown } from "./FilterDropdown";
import { ActionsMenu } from "./ActionsMenu";
import { AddNoteModal } from "./AddNoteModal";
import { AssignCourierModal } from "./AssignCourierModal";
import { ChangeStatusModal } from "./ChangeStatusModal";
import { DeleteModal } from "./DeleteModal";
import { CalendarView } from "./CalendarView";
import { RouteMapModal } from "./RouteMapModal";
import { ChangeRequestRow } from "./ChangeRequestRow";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

const COURIER_COLORS = [
  "#6366f1", // indigo
  "#0891b2", // cyan
  "#059669", // emerald
  "#d97706", // amber
  "#dc2626", // red
  "#7c3aed", // violet
  "#2563eb", // blue
  "#c026d3", // fuchsia
  "#ea580c", // orange
  "#0d9488", // teal
];

export default function HomePage() {
  const navigate    = useNavigate();
  const { isAdmin, isCourier } = useAuth();
  const queryClient = useQueryClient();

  // ── Week pagination ─────────────────────────────────────────────────────────
  const [weekOffset, setWeekOffset] = useState(0);

  // ── Data ────────────────────────────────────────────────────────────────────
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["scheduleItems", "week", weekOffset],
    queryFn:  () =>
      weekOffset === 0
        ? fetchScheduleItems({ week: "current", ...(isAdmin && { include_change_requests: "true" }) })
        : fetchScheduleItems({ week_offset: String(weekOffset), ...(isAdmin && { include_change_requests: "true" }) }),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const partnerNames = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [partners]);

  const partnerTypes = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.type; });
    return map;
  }, [partners]);

  // Admin needs the full user list for consistent courier color assignment across all items.
  // Courier/partner roles use contact_user embedded in each schedule item instead.
  const { data: allUsers = [] } = useQuery({
    queryKey: ["users"],
    queryFn:  () => fetchUsers(),
    enabled:  isAdmin,
  });

  const courierInfo = React.useMemo(() => {
    const nameMap: Record<number, string> = {};
    const colorMap: Record<number, string> = {};
    if (isAdmin) {
      const couriers = allUsers.filter((u) => u.role === "bridge13_courier" && u.active);
      couriers.forEach((u, i) => {
        nameMap[u.id] = `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`;
        colorMap[u.id] = COURIER_COLORS[i % COURIER_COLORS.length];
      });
    } else {
      // Build from embedded contact_user on each item (already included in API response)
      let colorIdx = 0;
      items.forEach((item) => {
        if (item.contact_user_id && item.contact_user && !nameMap[item.contact_user_id]) {
          const u = item.contact_user;
          nameMap[u.id] = `${u.name_1}${u.name_2 ? ` ${u.name_2}` : ""}`;
          colorMap[u.id] = COURIER_COLORS[colorIdx++ % COURIER_COLORS.length];
        }
      });
    }
    return { names: nameMap, colors: colorMap };
  }, [isAdmin, allUsers, items]);

  const partnerIds = React.useMemo(
    () => [...new Set(items.map((i) => i.partner_entity_id))],
    [items]
  );

  const notesQueries = useQuery({
    queryKey: ["notes", "home", partnerIds],
    queryFn: async () => {
      const results = await Promise.all(partnerIds.map((id) => fetchNotes(id)));
      const map: Record<number, Note[]> = {};
      partnerIds.forEach((id, i) => { map[id] = results[i]; });
      return map;
    },
    enabled: partnerIds.length > 0,
  });

  const notesByPartner = notesQueries.data ?? {};

  // ── View toggle (deep-linkable via ?view=calendar) ──────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const view = (searchParams.get("view") === "calendar" ? "calendar" : "list") as "list" | "calendar";
  const setView = (v: "list" | "calendar") =>
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("view", v); return next; });

  // ── Modal state ─────────────────────────────────────────────────────────────
  const [activeMenu, setActiveMenu]       = useState<number | null>(null);
  const [noteTarget, setNoteTarget]       = useState<ScheduleItem | null>(null);
  const [courierTarget, setCourierTarget] = useState<ScheduleItem | null>(null);
  const [statusTarget, setStatusTarget]   = useState<ScheduleItem | null>(null);
  const [deleteTarget, setDeleteTarget]   = useState<ScheduleItem | null>(null);
  const [routeTarget, setRouteTarget]     = useState<{ date: Date; items: ScheduleItem[] } | null>(null);

  // ── Scheduler tools (admin only) ───────────────────────────────────────────
  const [showTools, setShowTools] = useState(false);

  const schedulerMutation = useMutation({
    mutationFn: () => runScheduler(weekOffset),
    onSuccess:  () => { invalidate(); setShowTools(false); },
  });

  const recipientMutation = useMutation({
    mutationFn: () => runRecipientScheduler(weekOffset),
    onSuccess:  () => { invalidate(); setShowTools(false); },
  });

  // ── Open change-request count (admin only) — drives the banner ─────────────
  const { data: openCRItems = [] } = useQuery({
    queryKey: ["scheduleItems", "openChangeRequests"],
    queryFn:  () => fetchScheduleItemsWithChangeRequests("requested"),
    enabled:  isAdmin,
  });
  const openCRCount = openCRItems.reduce(
    (sum, item) => sum + (item.change_requests ?? []).filter((r) => r.status === "requested").length,
    0,
  );

  // ── Filters (admin only) ────────────────────────────────────────────────────
  const [showFilters, setShowFilters]                     = useState(false);
  const [filterUnassigned, setFilterUnassigned]           = useState(false);
  const [filterPickups, setFilterPickups]                 = useState(false);
  const [filterDropoffs, setFilterDropoffs]               = useState(false);
  const [filterBlackouts, setFilterBlackouts]             = useState(true);
  const [filterChangeRequests, setFilterChangeRequests]   = useState(false);
  const hasActiveFilters = filterUnassigned || filterPickups || filterDropoffs || filterChangeRequests;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["scheduleItems"] });

  // ── Week grid ───────────────────────────────────────────────────────────────
  const weekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset);
  const grouped = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(weekStart, i);
    return {
      label: DAYS[i],
      date,
      items: items.filter((item) => {
        if (!isSameDay(parseISO(item.scheduled_at), date)) return false;
        if (filterUnassigned && item.contact_user_id) return false;
        const type = partnerTypes[item.partner_entity_id];
        const isPickup = type !== "distributor";
        if (filterBlackouts && item.status === "blacked_out") return false;
        if (filterPickups && !isPickup) return false;
        if (filterDropoffs && isPickup) return false;
        if (filterChangeRequests) {
          const hasOpenCR = (item.change_requests ?? []).some((r) => r.status === "requested");
          if (!hasOpenCR) return false;
        }
        return true;
      }),
    };
  });

  if (isLoading) return <Spinner className="mt-20" />;

  const weekNav = (
    <div className="flex items-center gap-1 mt-1">
      <button
        onClick={() => setWeekOffset((o) => o - 1)}
        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors"
        title="Previous week"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        disabled={weekOffset === 0}
        onClick={() => setWeekOffset(0)}
        className={`px-2 py-1 text-xs rounded-lg border border-gray-200 text-gray-500 ${weekOffset === 0 ? "bg-gray-200" : "bg-white hover:bg-gray-50"} transition-colors`}
      >
        Today
      </button>
      <button
        onClick={() => setWeekOffset((o) => o + 1)}
        className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 transition-colors"
        title="Next week"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      {/* ── Open change-requests banner (admin only) ────────────────────────── */}
      {isAdmin && openCRCount > 0 && (
        <div className="flex items-center justify-between gap-3 mb-5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <span className="text-sm font-medium">
              Change requests to review
              <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-amber-200 text-amber-900 font-semibold">
                {openCRCount}
              </span>
            </span>
          </div>
          <button
            onClick={() => navigate("/change-requests")}
            className="text-sm font-medium text-amber-700 hover:text-amber-900 underline transition-colors shrink-0"
          >
            Review requests →
          </button>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <PageHeaderWithBodyAction
            action={weekNav}
            title={weekOffset === 0 ? "This week" : weekOffset === 1 ? "Next week" : weekOffset === -1 ? "Last week" : `Week of ${format(weekStart, "MMMM d")}`}
            subtitle={`${format(weekStart, "MMMM d")} – ${format(addDays(weekStart, 6), "MMMM d, yyyy")}`}
          />
        </div>
        <div className="flex items-center gap-2 mt-8">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden bg-white">
            <button
              onClick={() => setView("list")}
              className={`p-2 transition-colors ${view === "list" ? "bg-gray-800 text-white" : "text-gray-500 hover:bg-gray-50"}`}
              title="List view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`p-2 transition-colors border-l border-gray-200 ${view === "calendar" ? "bg-gray-800 text-white" : "text-gray-500 hover:bg-gray-50"}`}
              title="Calendar view"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </button>
          </div>
          {isAdmin && (
            <>
              <SchedulerToolsMenu
                isOpen={showTools}
                onToggle={() => setShowTools(!showTools)}
                onClose={() => setShowTools(false)}
                onRunScheduler={() => schedulerMutation.mutate()}
                onRunRecipientScheduler={() => recipientMutation.mutate()}
                isRunningScheduler={schedulerMutation.isPending}
                isRunningRecipient={recipientMutation.isPending}
              />
              <FilterDropdown
                isOpen={showFilters}
                onToggle={() => setShowFilters(!showFilters)}
                onClose={() => setShowFilters(false)}
                hasActiveFilters={hasActiveFilters}
                filterUnassigned={filterUnassigned}
                filterPickups={filterPickups}
                filterDropoffs={filterDropoffs}
                filterBlackouts={filterBlackouts}
                filterChangeRequests={filterChangeRequests}
                onToggleUnassigned={() => setFilterUnassigned(!filterUnassigned)}
                onTogglePickups={() => setFilterPickups(!filterPickups)}
                onToggleDropoffs={() => setFilterDropoffs(!filterDropoffs)}
                onToggleBlackouts={() => setFilterBlackouts(!filterBlackouts)}
                onToggleChangeRequests={() => setFilterChangeRequests(!filterChangeRequests)}
                onClear={() => {
                  setFilterUnassigned(false);
                  setFilterPickups(false);
                  setFilterDropoffs(false);
                  setFilterBlackouts(true);
                  setFilterChangeRequests(false);
                }}
              />
            </>
          )}
        </div>
      </div>

      {view === "calendar" ? (
        <CalendarView
          items={items}
          weekStart={weekStart}
          partnerNames={partnerNames}
          partnerTypes={partnerTypes}
          courierInfo={courierInfo}
          notesByPartner={notesByPartner}
          onSelectEvent={(itemId) => navigate(`/schedule-items/${itemId}`)}
        />
      ) : (
        <div className="mt-6 space-y-6">
        {grouped.map(({ label, date, items: dayItems }) => (
          <div key={label}>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {label} &mdash; {format(date, "MMM d")}
              </h2>
              {isCourier && dayItems.length > 0 && (
                <button
                  onClick={() => setRouteTarget({ date, items: dayItems })}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  View route
                </button>
              )}
            </div>

            {dayItems.length === 0 ? (
              <p className="text-sm text-gray-300 pl-1">No pickups</p>
            ) : (
              <div className="space-y-2">
                {dayItems.map((item) => {
                  const partnerName = partnerNames[item.partner_entity_id] ?? `Partner #${item.partner_entity_id}`;
                  const partnerType = partnerTypes[item.partner_entity_id];
                  const isPickup = partnerType !== "distributor";
                  const actionLabel = isPickup ? "Pickup" : "Drop off";
                  const actionVerb = isPickup ? "from" : "at";
                  const allPartnerNotes = notesByPartner[item.partner_entity_id] ?? [];
                  const itemNotes = allPartnerNotes.filter(
                    (n) => !n.schedule_item_id || n.schedule_item_id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      className="card flex"
                      style={item.contact_user_id && courierInfo.colors[item.contact_user_id]
                        ? { borderLeft: `4px solid ${courierInfo.colors[item.contact_user_id]}` }
                        : { borderLeft: "4px solid border-gray-200" }
                      }
                    >
                      <div className="flex-1">
                        <div
                          onClick={() => navigate(`/schedule-items/${item.id}`)}
                          className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          {item.contact_user_id && (
                            <div className="shrink-0">
                              {item.contact_user?.profile_photo_url ? (
                                <img
                                  src={item.contact_user.profile_photo_url}
                                  className="w-8 h-8 rounded-full object-cover"
                                  alt=""
                                />
                              ) : (
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                                  style={{ backgroundColor: courierInfo.colors[item.contact_user_id] ?? "#9ca3af" }}
                                >
                                  {(courierInfo.names[item.contact_user_id] ?? "?").charAt(0)}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                              <span className={`shrink-0 px-1.5 py-0.5 rounded text-xl font-medium ${
                                isPickup
                                  ? "bg-green-100 text-green-700"
                                  : "bg-indigo-100 text-indigo-700"
                              }`}>
                                {actionLabel}
                              </span>{actionVerb} {partnerName}
                            </p>
                            {((item.deliver_tos?.length ?? 0) > 0 || (item.received_froms?.length ?? 0) > 0) &&
                              <p className="text-xs text-gray-500 flex items-center gap-2 py-2">
                                <span className="shrink-0 px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                  {(item.deliver_tos?.length ?? 0) > 0
                                    ? `Deliver to ${item.deliver_tos!.map((p) => p.name).join(", ")}`
                                    : `Receive from ${item.received_froms!.map((p) => p.name).join(", ")}`}
                                </span>
                              </p>
                            }
                            <p className="text-xs text-gray-500">
                              {parseISO(item.scheduled_at).toLocaleTimeString([], {
                                hour: "numeric",
                                minute: "2-digit",
                                timeZoneName: "short",
                              })}
                              {item.contact_user_id && courierInfo.names[item.contact_user_id] && (
                                <>
                                  {" · "}
                                  <span style={{ color: courierInfo.colors[item.contact_user_id] }} className="font-medium">
                                    {courierInfo.names[item.contact_user_id]}
                                  </span>
                                </>
                              )}
                              {item.contact_user_id && !courierInfo.names[item.contact_user_id] && (
                                <> · Courier #{item.contact_user_id}</>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <StatusBadge status={item.status} />
                            {isAdmin && (
                              <ActionsMenu
                                isOpen={activeMenu === item.id}
                                onToggle={(e) => {
                                  e.stopPropagation();
                                  setActiveMenu(activeMenu === item.id ? null : item.id);
                                }}
                                onClose={() => setActiveMenu(null)}
                                onAddNote={() => { setActiveMenu(null); setNoteTarget(item); }}
                                onAssignCourier={() => { setActiveMenu(null); setCourierTarget(item); }}
                                onChangeStatus={() => { setActiveMenu(null); setStatusTarget(item); }}
                                onDelete={() => { setActiveMenu(null); setDeleteTarget(item); }}
                              />
                            )}
                          </div>
                        </div>
                        {itemNotes.length > 0 && (
                          <div className="border-t border-gray-100 px-4 py-2 space-y-1.5 bg-gray-50/50">
                            {itemNotes.map((n) => (
                              <div key={n.id} className="flex items-start gap-2 text-xs">
                                <span className={`shrink-0 px-1.5 py-0.5 rounded font-medium ${
                                  n.schedule_item_id
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}>
                                  {n.schedule_item_id ? "This pickup note" : "Partner note"}
                                </span>
                                <span className="text-gray-500">{n.note}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {isAdmin && (() => {
                          const pending = (item.change_requests ?? []).filter((r) => r.status === "requested");
                          return pending.length > 0 ? (
                            <div className="border-t border-amber-100 px-4 py-2.5 space-y-2 bg-amber-50/40">
                              {pending.map((cr) => (
                                <ChangeRequestRow
                                  key={cr.id}
                                  request={cr}
                                  scheduleItemId={item.id}
                                />
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        </div>
      )}

      {noteTarget && (
        <AddNoteModal
          item={noteTarget}
          partnerName={partnerNames[noteTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setNoteTarget(null)}
          onSuccess={() => {
            invalidate();
            queryClient.invalidateQueries({ queryKey: ["notes", "home"] });
            setNoteTarget(null);
          }}
        />
      )}

      {courierTarget && (
        <AssignCourierModal
          item={courierTarget}
          partnerName={partnerNames[courierTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setCourierTarget(null)}
          onSuccess={() => { invalidate(); setCourierTarget(null); }}
        />
      )}

      {statusTarget && (
        <ChangeStatusModal
          item={statusTarget}
          partnerName={partnerNames[statusTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setStatusTarget(null)}
          onSuccess={() => { invalidate(); setStatusTarget(null); }}
        />
      )}

      {deleteTarget && (
        <DeleteModal
          item={deleteTarget}
          partnerName={partnerNames[deleteTarget.partner_entity_id] ?? "Unknown"}
          onClose={() => setDeleteTarget(null)}
          onSuccess={() => { invalidate(); setDeleteTarget(null); }}
        />
      )}

      {routeTarget && (
        <RouteMapModal
          items={routeTarget.items}
          date={routeTarget.date}
          partnerNames={partnerNames}
          partnerTypes={partnerTypes}
          onClose={() => setRouteTarget(null)}
        />
      )}
    </div>
  );
}
